/**
 * CapacityPanel — delivery console widget: compact resource capacity card.
 *
 * Shows:
 *   - Fully-booked KPI line: "{n} fully booked this month" (red when n > 0,
 *     muted when 0). Falls back to "{total} active bottlenecks" when
 *     fullyBookedCount is null or undefined.
 *   - Top 6 most-occupied resources sorted by current-month utilisation desc,
 *     tie-broken by backlog desc then member name. Each row: member name,
 *     "Free {date}", "{backlogMd} md", and a mini backlog bar whose length is
 *     the backlog relative to the busiest and whose fill colour reflects that
 *     same fill fraction (fullest = red) via capacityBarColor().
 *   - Footer: react-router Link to /workload — always rendered, even when empty.
 *
 * Card chrome matches Donut.tsx / ThroughputBar.tsx exactly.
 * All colours are CSS custom-property var(--…) strings — no raw hex.
 */
import { useMemo } from 'react';
import { Link } from 'react-router';
import type { Bottleneck } from '@/api/dashboard';
import { capacityBarColor } from '@/lib/capacity';
import { sectionCardStyle, hatchedFillStyle, hatchedTrackStyle } from './chartTokens';
import { CardHeader, IconPeople } from './CardHeader';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CapacityPanelProps {
  bottlenecks: Bottleneck[];
  fullyBookedCount?: number | null;
  utilByMember: Record<string, number>;
  /**
   * Heading text. Defaults to the panel's own name so standalone usage
   * (WorkloadCapacityPage) is unchanged; the dashboard CapacityHost passes
   * the widget's title through so renaming the widget is actually visible.
   */
  title?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CapacityPanel({ bottlenecks, fullyBookedCount, utilByMember, title = 'Resource capacity' }: CapacityPanelProps) {
  const hasFullyBooked = typeof fullyBookedCount === 'number';

  // Sort by highest current-month utilisation desc, tie-break by backlog desc
  // then member name asc; take top 5 (the owner-set contract for this panel — and the
  // adjacent Work-items-with-open-RAIDs widget in the panel row).
  const top5 = useMemo(
    () =>
      [...bottlenecks]
        .sort(
          (a, b) =>
            (utilByMember[b.memberId] ?? 0) - (utilByMember[a.memberId] ?? 0) ||
            b.backlogMd - a.backlogMd ||
            a.memberName.localeCompare(b.memberName),
        )
        .slice(0, 5),
    [bottlenecks, utilByMember],
  );

  const maxBacklog = useMemo(
    () => Math.max(...top5.map((b) => b.backlogMd), 1),
    [top5],
  );

  const isEmpty = bottlenecks.length === 0;

  return (
    <section
      data-testid="capacity-panel"
      style={sectionCardStyle({ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' })}
    >
      {/* Header — §2.11 anatomy: [icon 18/1.5] 10 [h2 17/600]. */}
      <CardHeader icon={<IconPeople />} title={title} sub="Most-occupied resources · fully booked this month" />

      {/* Fully-booked KPI line — inset well, one luminance step down (§2.11). */}
      <div
        style={{
          marginTop: 14,
          display: 'flex',
          alignItems: 'baseline',
          gap: 6,
          background: 'var(--inset)',
          borderRadius: 'var(--radius-inset)',
          padding: '10px 12px',
        }}
      >
        {hasFullyBooked ? (
          <>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 20,
                fontWeight: 700,
                color:
                  fullyBookedCount! > 0
                    ? 'var(--sem-red)'
                    : 'var(--text-mid)',
                lineHeight: 1,
              }}
            >
              {fullyBookedCount}
            </span>
            {/* §1.5 — sentence case, no all-caps labels. */}
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 11,
                color: 'var(--text-low)',
              }}
            >
              fully booked this month
            </span>
          </>
        ) : (
          <>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 20,
                fontWeight: 700,
                color: 'var(--text-mid)',
                lineHeight: 1,
              }}
            >
              {bottlenecks.length}
            </span>
            {/* §1.5 — sentence case, no all-caps labels. */}
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 11,
                color: 'var(--text-low)',
              }}
            >
              active bottlenecks
            </span>
          </>
        )}
      </div>

      {/* Body: bottleneck list or empty hint. minHeight:0 + overflow:hidden —
          a flex child's default min-height:auto let the member list force the
          card past its grid cell and paint over the widgets below (caught live
          in the M4 gate on a palette-default 6x3 cell); the card now clips and
          the sibling footer link stays visible. */}
      <div data-testid="capacity-panel-body" style={{ marginTop: 14, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {isEmpty ? (
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              color: 'var(--text-low)',
            }}
          >
            No resource bottlenecks
          </span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {top5.map((b) => {
              const barPct = maxBacklog > 0 ? (b.backlogMd / maxBacklog) * 100 : 0;
              const barHue = capacityBarColor(barPct);
              return (
                <div key={b.memberId}>
                  {/* Row: name + meta */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span
                      title={b.memberName}
                      style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'var(--text-hi)',
                        flex: '0 0 110px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {b.memberName}
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 12,
                        color: 'var(--text-mid)',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {`Free ${b.nextFreeDate.slice(0, 10)}`}
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--text-hi)',
                        fontVariantNumeric: 'tabular-nums',
                        marginLeft: 'auto',
                      }}
                    >
                      {`${b.backlogMd} md`}
                    </span>
                  </div>
                  {/* Mini backlog bar — §1.8/§2.10 hatched meter: 45° stripe fill on a
                      same-hue dim track (never gray), colour reflects backlog fill. */}
                  <div
                    style={{
                      height: 6,
                      borderRadius: 999,
                      overflow: 'hidden',
                      ...hatchedTrackStyle(barHue),
                    }}
                  >
                    <div
                      data-testid="capacity-bar-fill"
                      style={{
                        height: '100%',
                        width: `${barPct}%`,
                        borderRadius: 999,
                        backgroundSize: '6px 6px',
                        transition: 'width 0.4s cubic-bezier(0.16,1,0.3,1)',
                        ...hatchedFillStyle(barHue),
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer link — §2.11 card-corner "See all" treatment (12/500 neutral
          --card-link), not lime: lime is reserved for logo/CTA/selection/
          avatar initials, never a plain navigation link (§1.2/§1.8.6). */}
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Link
          to="/workload"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--card-link)',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none';
          }}
        >
          View full capacity →
        </Link>
      </div>
    </section>
  );
}
